import React, {
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { Link, useLocation } from "react-router-dom";

import {
  MoreInfoBlock,
  SpriteIcon,
  StreetSearchBox,
  SweeperMap,
} from "../components";
import { StreetSweepingSchedule, SweeperTruckGpsData } from "../models";
import {
  convertEpochToDisplayDate,
  formattedStreetAddress,
  getLastItem,
  getNextSweepDate,
} from "../utils";
import { useScrollToTop } from "../hooks";
import { useApiQuery } from "../contexts";

import {
  LastStreetSweepByStreetQueryType,
  LAST_STREET_SWEEP_BY_STREET_QUERY,
} from "../graphql";
import { SeoHelmet } from "../components";
import { StreetDatasetContext } from "../contexts";

interface ResultsPageLocationState {
  searchStreet: StreetSweepingSchedule;
}

const DATE_NOT_AVAILABLE = "NA";
interface SweepScheduleDisplayState {
  lastSweepDate: string;
  nextSweepDateRange: string;
}

export default function ResultPage(): ReactElement {
  useScrollToTop();
  const { state, search } = useLocation<ResultsPageLocationState>();

  const [currentStreet, setCurrentStreet] = useState<StreetSweepingSchedule>();
  const [lookupStreet, setLookupStreet] = useState<StreetSweepingSchedule>();
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [truckGpsData, setTruckGpsData] = useState<SweeperTruckGpsData[]>();

  const [
    sweepScheduleDisplay,
    setSweepScheduleDisplay,
  ] = useState<SweepScheduleDisplayState>({
    lastSweepDate: "",
    nextSweepDateRange: "",
  });

  const queryParams = new URLSearchParams(search);
  const streetName = queryParams.get("streetName");
  const suburb = queryParams.get("suburb");
  const postcode = queryParams.get("postcode");

  const { dataset } = useContext(StreetDatasetContext);

  const getStreetDataByAddress = useCallback(
    (streetName: string, suburb: string, postcode: string) => {
      return dataset.find((item) => {
        return (
          item.streetName === streetName &&
          item.suburb === suburb &&
          item.postcode === postcode
        );
      });
    },
    [dataset]
  );

  useEffect(() => {
    if (state && state.searchStreet) {
      setSweepScheduleDisplay({
        lastSweepDate: "Loading ...",
        nextSweepDateRange: "Loading ...",
      });
      setCurrentStreet(state.searchStreet);
    } else {
      if (streetName && suburb && postcode) {
        setSweepScheduleDisplay({
          lastSweepDate: "Loading ...",
          nextSweepDateRange: "Loading ...",
        });
        setCurrentStreet(getStreetDataByAddress(streetName, suburb, postcode));
      }
    }
  }, [state, streetName, suburb, postcode, getStreetDataByAddress]);

  const { loading, data } = useApiQuery<LastStreetSweepByStreetQueryType>(
    LAST_STREET_SWEEP_BY_STREET_QUERY,
    {
      variables: {
        streetName: currentStreet?.streetName,
        suburbName: currentStreet?.suburb,
      },
      skip: !currentStreet,
    }
  );

  const graphqlLastStreetSweepByStreet = data
    ? data.lastStreetSweepByStreet
    : null;

  useEffect(() => {
    if (graphqlLastStreetSweepByStreet && currentStreet) {
      const pathCoordinates = graphqlLastStreetSweepByStreet.map(
        ({ gps_lat, gps_lon, gps_time }) => ({
          lat: gps_lat,
          lng: gps_lon,
          epochTime: parseInt(gps_time),
        })
      );
      setTruckGpsData(pathCoordinates);

      const lastStreetSweepData = getLastItem(graphqlLastStreetSweepByStreet);
      if (lastStreetSweepData) {
        const nextSweepDateRange = getNextSweepDate(
          lastStreetSweepData.gps_time
        );
        setSweepScheduleDisplay({
          lastSweepDate: convertEpochToDisplayDate(
            lastStreetSweepData.gps_time
          ),
          nextSweepDateRange,
        });
      } else {
        setSweepScheduleDisplay({
          lastSweepDate: DATE_NOT_AVAILABLE,
          nextSweepDateRange: DATE_NOT_AVAILABLE,
        });
      }
    }
  }, [loading, currentStreet, graphqlLastStreetSweepByStreet]);

  const [clearLookup, setClearLookup] = useState(false);
  useEffect(() => {
    if (sweepScheduleDisplay) {
      setClearLookup(false);
    }
  }, [sweepScheduleDisplay]);

  const displayStreetAddress = currentStreet
    ? formattedStreetAddress(currentStreet)
    : "";

  const { lastSweepDate, nextSweepDateRange } = sweepScheduleDisplay;

  return (
    <main className="main">
      <SeoHelmet
        title={
          displayStreetAddress
            ? `${displayStreetAddress} - Street Sweeper Tracker`
            : "Street Sweeper Tracker"
        }
        description="There are almost 2,400 streets in our city and we aim to sweep each street at least once every six weeks."
      />
      <section className="tracker-section bg-gray">
        <div className="tracker-section__container container">
          <div className="tracker-section__box">
            <div className="tracker-section__descr">
              <div className="tracker-section__heading">
                <Link to="/" className="tracker-section__back">
                  <SpriteIcon
                    iconName="arrow-left"
                    className="tracker-section__back-icon"
                  />
                </Link>
                <h1 className="tracker-section__title h3">
                  Street Sweep Tracker
                </h1>
              </div>
              <div className="tracker-section__address h4">
                <SpriteIcon
                  iconName="location"
                  className="tracker-section__address-icon"
                />
                <span className="tracker-section__address-text">
                  {displayStreetAddress}
                </span>
              </div>
              <label className="tracker-section__field field">
                <span className="field__label">
                  <span className="field__label-text">Last sweep was:</span>
                </span>
                <span className="field__input-wrap">
                  <input
                    type="text"
                    name="last sweep"
                    className="field__input field__input_with-icon input"
                    placeholder="Last sweep in"
                    value={
                      lastSweepDate === DATE_NOT_AVAILABLE
                        ? "Date not available..."
                        : lastSweepDate
                    }
                    readOnly
                  />
                  <SpriteIcon iconName="clock" className="field__input-icon" />
                </span>
              </label>
              {lastSweepDate === DATE_NOT_AVAILABLE && (
                <div
                  style={{
                    margin: "10px 0 20px",
                    background: "#60C3AD",
                    borderRadius: "5px",
                    padding: "12px 16px",
                    color: "#fff",
                  }}
                >
                  Our street sweeper data for this street has not been updated
                  yet. Please check back soon.
                </div>
              )}
              <label className="tracker-section__field field">
                <span className="field__label">
                  <span className="field__label-text">Next sweep will be:</span>
                </span>
                <span className="field__input-wrap">
                  <input
                    type="text"
                    name="next sweep"
                    className="field__input field__input_with-icon input"
                    placeholder="Next sweep in"
                    value={
                      nextSweepDateRange === DATE_NOT_AVAILABLE
                        ? "Date not available..."
                        : nextSweepDateRange
                    }
                    readOnly
                  />
                  <SpriteIcon
                    iconName="calendar"
                    className="field__input-icon"
                  />
                </span>
              </label>
              <form className="tracker-section__search">
                <label className="tracker-section__field field">
                  <span className="field__label">
                    <span className="field__label-title h4">
                      Search another street
                    </span>
                  </span>
                  <span className="field__input-wrap">
                    <StreetSearchBox
                      onSelectionChange={setLookupStreet}
                      handleLookupError={setLookupError}
                      inputFieldClassName="field__input field__input_with-icon field__input_with-btn"
                      clearLookup={clearLookup}
                    />
                    <SpriteIcon
                      iconName="location"
                      className="field__input-icon"
                    />
                  </span>
                </label>
                <button
                  className="tracker-section__search-btn button button_color_yellow button_size_medium button_shadow_default"
                  onClick={(e) => {
                    e.preventDefault();
                    if (lookupStreet) {
                      if (currentStreet !== lookupStreet) {
                        setCurrentStreet(lookupStreet);
                        setSweepScheduleDisplay({
                          lastSweepDate: "Loading...",
                          nextSweepDateRange: "Loading...",
                        });
                        setClearLookup(true);
                      }
                    }
                  }}
                >
                  Go
                </button>
              </form>
              {lookupError && (
                <div style={{ marginTop: "10px" }}>{lookupError}</div>
              )}
            </div>
            <SweeperMap
              currentStreet={currentStreet}
              truckGpsData={truckGpsData}
            />
          </div>
        </div>
      </section>

      <MoreInfoBlock />
    </main>
  );
}
